/**
 * venue_binding.ts — WHICH BUILDING IS THE TOWN'S TAVERN (the interiors programme, slice E, 2026-10-01). PURE: no
 * imports, no rows, no three — the bake, the server's post reducers, the client's venue module, the maps and the gate
 * (`verify:venue-binding`) all read these functions.
 *
 * MIRROR: `client/src/lib/venue_binding.ts` ≡ `server/src/lib/venue_binding.ts`, BYTE-IDENTICAL (the gate compares the
 * files). The baked table is a second mirror pair: `shared/venue_bindings.generated.ts` ≡
 * `server/src/lib/venue_bindings.generated.ts` (`npm run bake:venues` — REBAKE after `bake:proxies` / `bake:map` /
 * `bake:footprints`, or after a rule constant below changes).
 *
 *   A VENUE WAS A POINT. A town's tavern is a slot of the town plan (`town_layout.ts`); the publican's post, its duty
 *   radius and its customers all measured from that point, and the tavern BUILDING hosted nothing. A BINDING names
 *   the one enterable building of a site that IS the venue: the post is then worked inside it.
 *
 *   THE RULE (`pickVenue`) — deterministic from what client and server both ship (the proxies / map bakes):
 *     candidates = the site's placements — buildings AND landmarks — that the interior admission rule opens with
 *                  `VENUE_SIZE_MARGIN_M` to spare, whose door reaches a street cell within `VENUE_DOOR_MAX_M`, and
 *                  that the assembly really STANDS at their baked size (THE STANDING LAW, below);
 *     class 0    = placed by the plan FOR the venue's function, within `VENUE_PREFER_M` of the slot;
 *     class 1    = any other non-residential function, within `VENUE_BOUND_M`;
 *     class 2    = a residential building, within `VENUE_BOUND_M`;
 *     a venue binds only up to its `VenueDef.maxClass` — by DEFAULT 0, THE STRICT RULE (only a building the plan placed
 *     for the venue). The tavern and the clinic carry `maxClass: 1` (OPTION B, below): class 0 first, else another
 *     NON-residential building, NEVER a residential one;
 *     a building placed for ANOTHER venue kind's function is that kind's — never this kind's fallback (a tavern never
 *     takes the town's medical building, a clinic never a cantina's: without it the tavern, bound first, took site
 *     53's only medical building as its class 1 and the clinic lost its class 0);
 *     the lowest class wins, then the nearest to the slot, then the lower placement index;
 *     a building is bound to ONE venue (the clinic never takes the tavern's building);
 *     no candidate → no binding: the town keeps its outdoor venue (every hamlet does — its huts are under the floor).
 *
 *   THE OWNER'S RULE (2026-10-01, replacing the first cut the same day): "tavern should only bind to a hospitality
 *   type building", "clinics should follow the same strict rule", and a landmark MAY be a venue. The first cut also
 *   took any non-residential building (class 1) and, failing that, a home (class 2), and refused every landmark: 81
 *   taverns and 68 clinics, of which 62 and 62 were not placed for the purpose (a tavern on the ground floor of a
 *   103 m apartment tower). Strict cost 81 → 19 taverns and 68 → 7 clinics, and the owner then chose (2026-10-01 20:35,
 *   of four options): "B for my option on tavern and clinics" — OPTION B: a building placed for the venue's function
 *   first (class 0), else any other NON-residential building (class 1), never a residential one (class 2). It is DATA:
 *   `maxClass: 1` on those two defs; a future venue kind is strict (0) unless its def says otherwise. `cls` in the
 *   table says which a row is (0 or 1 today).
 *   A LANDMARK CARRIES NO PLAN FUNCTION in today's bakes (the map file's `fn` is empty for every monument), so a
 *   monument is class 1 at best: under option B an ADMITTED monument within `VENUE_BOUND_M` of the slot binds when no
 *   building placed for the venue stands in reach and it is the nearest non-residential candidate; a landmark the plan
 *   places FOR a venue's function binds like any class-0 building.
 *
 *   THE STANDING LAW (2026-10-01, found by the probe at town 14). The bakes list what the assembly ATTEMPTS; the
 *   assembly's own non-overlap filter then skips a blocked placement or steps it down a shrink ladder (a town's second
 *   monument beside its first; a tower crowded by a taller one). Such a building has no door in the game, or a smaller
 *   box than the table says — so `VenueCandidate.standing` (the bake replays the assembly's filter) must be true. Of
 *   the first option-B table's 135 rows 15 did not stand (11 monuments, 4 buildings — 2 of them strict class-0 clinics).
 *
 *   WHAT THE SERVER KNOWS (before the shared-rooms slice mirrors the planner): the building's oriented box, its door
 *   and the street cell in front of it. `insideVenue` is that box (the 60 m walkable core) plus a doorstep pad — NOT
 *   the counter, not a room, not a floor: the server's walking position is 2D. The counter anchor is the client's.
 *
 *   THE LEVER: `platform_flag interior_venues_v1` (ABSENT = ON). Off → every caller takes today's slot path; a post
 *   row is never rewritten (its `venueX/venueZ` is always the SLOT; the building is resolved when it is asked for).
 */

/** The platform flag behind every use of a binding (absent = ON; `reducers/platform.ts KNOWN_FLAGS`). */
export const VENUE_FLAG = 'interior_venues_v1';

export type VenueKindKey = 'tavern' | 'clinic';

export interface VenueDef {
  kind: VenueKindKey;
  /** The town-plan slot the venue was (and, unbound, still is). */
  slot: string;
  /** The service post that is worked there. */
  post: string;
  /** The plan functions a building is placed FOR that make it this venue's natural home (class 0). */
  fns: readonly string[];
  /**
   * The WIDEST class this venue may bind (see `venueClass`): 0 = only a building the plan placed for it — THE STRICT
   * RULE, and the default when the field is absent, so a new venue kind is strict unless its def says otherwise;
   * 1 = also any other non-residential building; 2 = also a home.
   */
  maxClass?: 0 | 1 | 2;
  /** The interior purpose a bound building resolves to. */
  purpose: 'hospitality' | 'clinic';
}

/** In binding order: a building taken by an earlier kind is never offered to a later one. */
export const VENUE_DEFS: readonly VenueDef[] = [
  { kind: 'tavern', slot: 'tavern', post: 'svc:publican', fns: ['entertainment', 'recreation'], maxClass: 1, purpose: 'hospitality' },
  { kind: 'clinic', slot: 'guardpost', post: 'svc:healer', fns: ['medical'], maxClass: 1, purpose: 'clinic' },
];
/** The widest class a venue kind may bind — strict (0) unless its def widens it. */
export function venueMaxClass(def: Pick<VenueDef, 'maxClass'>): number { return def.maxClass ?? 0; }
export function venueDef(kind: string): VenueDef | null {
  for (const d of VENUE_DEFS) if (d.kind === kind) return d;
  return null;
}
/** The venue a service post is worked at, when it has one (`svc:publican` → the tavern). */
export function venueDefOfPost(postKey: string): VenueDef | null {
  for (const d of VENUE_DEFS) if (d.post === postKey) return d;
  return null;
}

/** A building placed for the venue's own function may stand this far from the slot (m). */
export const VENUE_PREFER_M = 200;
/** Any other building must stand within this of the slot (m). */
export const VENUE_BOUND_M = 150;
/** The interior admission floor (a MIRROR of `building_interiors.ts SIZE_FLOOR.settlement`) … */
export const VENUE_FLOOR = { w: 7, d: 7, h: 4.6 } as const;
/** … and the margin a bound building keeps over it on every axis: the client admits by the model's real box, the bake
 *  by the ladder box — a building AT the floor could open on one and not the other. */
export const VENUE_SIZE_MARGIN_M = 1.5;
/** The walkable core of a monument is clamped to this (a MIRROR of `interiorSpec`'s 60 m). */
export const VENUE_CORE_MAX_M = 60;
/** The shell's inset from the footprint (a MIRROR of `building_interiors.ts WALL_IN`): the door stands this far inside the box. */
export const VENUE_WALL_IN_M = 0.45;
/** Presence at a bound venue: inside the core box, or within this of it (the doorstep, a model a little off its box). */
export const VENUE_PAD_M = 4;
/** The point in front of the door the customer's street cell is searched from (m outside the box). */
export const VENUE_DOOR_OUT_M = 2.5;
/** A candidate whose door is farther than this from any street cell is no venue (the door faces a wall or a pocket). */
export const VENUE_DOOR_MAX_M = 12;
/** A customer within this of the door cell has ARRIVED (the mover stops a body or two short of a goal in a crowd). */
export const VENUE_ARRIVE_M = 6;
/** A post row names its slot in f32 — a binding's slot within this of it is that slot. */
export const VENUE_SLOT_TOL_M = 2;
/** An enterable within this of a binding's seat (and of the same site) is the bound building. */
export const VENUE_MATCH_TOL_M = 1.5;

/** Functions a building is LIVED in for (class 2 — bound last: its residents' room becomes the venue). */
export const VENUE_RESIDENTIAL_FNS: readonly string[] = ['habitation', 'habitation_luxury', 'barracks'];

/** One placement of a site, as the bakes describe it. */
export interface VenueCandidate {
  /** The placement's index in the site's baked list (proxies `structs` ≡ map `b`, the same order). */
  index: number;
  key: string;
  /** World seat (the pad centre the assembly seats the bbox bottom-centre on), three's `rotation.y = yaw`. */
  x: number; z: number; yaw: number;
  /** The scaled natural box (m) and the seat's ground height. */
  w: number; d: number; h: number; gy: number;
  /** The plan's function for the pad ('' when it has none). */
  fn: string;
  landmark: boolean;
  /** The interior admission rule's answer for (key, w, h, d). */
  admitted: boolean;
  /** THE STANDING LAW: the assembly stands this placement at its baked scale (not skipped by its non-overlap filter,
   *  not stepped down the shrink ladder). The bake replays the filter; a placement that does not stand never binds. */
  standing: boolean;
}

export interface VenueBinding {
  /** The baked site id (a DISTRICT's own id — a capital's wards bind one each). */
  site: number;
  kind: VenueKindKey;
  index: number;
  key: string;
  x: number; z: number; yaw: number;
  w: number; d: number; h: number; gy: number;
  /** 0 = placed for the venue's function · 1 = another non-residential building · 2 = a residential one (never above the kind's `maxClass` — 1 for both kinds today, so never 2). */
  cls: number;
  fn: string;
  /** The town-plan slot this venue was (`player_post.venueX/Z` names it). */
  slotX: number; slotZ: number;
  /** The street cell in front of the door: where a customer waits, where a waypoint leads. */
  streetX: number; streetZ: number;
}

// ─── Geometry (three's convention: local +x → (cos yaw, −sin yaw), local +z → (sin yaw, cos yaw)) ─────────────────────

type Seat = Pick<VenueBinding, 'x' | 'z' | 'yaw' | 'w' | 'd'>;

/** Half extents of the walkable core's FOOTPRINT (the box before the shell inset). */
export function venueHalf(b: Pick<VenueBinding, 'w' | 'd'>): { hw: number; hd: number } {
  return { hw: Math.min(b.w, VENUE_CORE_MAX_M) / 2, hd: Math.min(b.d, VENUE_CORE_MAX_M) / 2 };
}
/** World → building-local (the inverse of `venueWorld`; the same turn `enterableAt` and `clampIndoor` make). */
export function venueLocal(b: Seat, x: number, z: number): { lx: number; lz: number } {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  const dx = x - b.x, dz = z - b.z;
  return { lx: c * dx - s * dz, lz: s * dx + c * dz };
}
/** Building-local → world (the visit's `toWorld`). */
export function venueWorld(b: Seat, lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  return { x: b.x + lx * c + lz * s, z: b.z - lx * s + lz * c };
}
/** Is a world point AT the venue: inside the core box, or within `pad` of it? (NaN is never inside.) */
export function insideVenue(b: Seat, x: number, z: number, pad: number = VENUE_PAD_M): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  const { lx, lz } = venueLocal(b, x, z);
  const { hw, hd } = venueHalf(b);
  return Math.abs(lx) <= hw + pad && Math.abs(lz) <= hd + pad;
}
/** The door: the middle of the local −Z face of the shell (`interiorSpec().door` through the building's yaw). */
export function venueDoor(b: Seat): { x: number; z: number } {
  return venueWorld(b, 0, -(venueHalf(b).hd - VENUE_WALL_IN_M));
}
/** The point in front of the door a street cell is looked for from. */
export function venueDoorOut(b: Seat): { x: number; z: number } {
  return venueWorld(b, 0, -(venueHalf(b).hd + VENUE_DOOR_OUT_M));
}

// ─── THE RULE ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Is a placement big enough, open and really there? A landmark counts like any building (it must be ADMITTED like one). */
export function venueEligible(c: VenueCandidate): boolean {
  if (!c.admitted || !c.standing) return false;
  const m = VENUE_SIZE_MARGIN_M;
  return c.w >= VENUE_FLOOR.w + m && c.d >= VENUE_FLOOR.d + m && c.h >= VENUE_FLOOR.h + m;
}
/** 0 = placed for this venue · 2 = lived in · 1 = everything else. */
export function venueClass(def: VenueDef, fn: string): number {
  if (def.fns.indexOf(fn) >= 0) return 0;
  return VENUE_RESIDENTIAL_FNS.indexOf(fn) >= 0 ? 2 : 1;
}

/** Is `fn` a function another venue kind is placed FOR? Such a building is RESERVED for that kind (never a fallback). */
export function venueReserved(def: VenueDef, fn: string): boolean {
  for (const d of VENUE_DEFS) if (d.kind !== def.kind && d.fns.indexOf(fn) >= 0) return true;
  return false;
}

/**
 * The one building of a site that is `def`'s venue, or null (the town keeps the slot).
 * `streetOf(c)` = the street cell the candidate's door reaches (the server's walking grid — the bake supplies it), or
 * null when there is none; it is asked only for candidates in winning order. `taken` = placement indices already bound.
 */
export function pickVenue(
  def: VenueDef, site: number, slot: { x: number; z: number }, cands: readonly VenueCandidate[], taken: ReadonlySet<number>,
  streetOf: (c: VenueCandidate) => { x: number; z: number } | null,
): VenueBinding | null {
  const pool: Array<{ c: VenueCandidate; cls: number; dist: number }> = [];
  for (const c of cands) {
    if (taken.has(c.index) || !venueEligible(c)) continue;
    const cls = venueClass(def, c.fn);
    if (cls > venueMaxClass(def)) continue; // the def's widest class (strict unless it widens it; never a home at 1)
    if (cls > 0 && venueReserved(def, c.fn)) continue; // another venue kind's own building is never a fallback
    const dist = Math.hypot(c.x - slot.x, c.z - slot.z);
    if (dist > (cls === 0 ? VENUE_PREFER_M : VENUE_BOUND_M)) continue;
    pool.push({ c, cls, dist });
  }
  pool.sort((a, b) => a.cls - b.cls || a.dist - b.dist || a.c.index - b.c.index);
  for (const p of pool) {
    const st = streetOf(p.c);
    if (!st) continue;
    const out = venueDoorOut(p.c);
    if (Math.hypot(st.x - out.x, st.z - out.z) > VENUE_DOOR_MAX_M) continue;
    const c = p.c;
    return {
      site, kind: def.kind, index: c.index, key: c.key, x: c.x, z: c.z, yaw: c.yaw, w: c.w, d: c.d, h: c.h, gy: c.gy,
      cls: p.cls, fn: c.fn, slotX: slot.x, slotZ: slot.z, streetX: st.x, streetZ: st.z,
    };
  }
  return null;
}

// ─── The baked table ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Numbers per row: site, kindIdx, index, keyIdx, x, z, yaw, w, d, h, gy, cls, fnIdx, slotX, slotZ, streetX, streetZ. */
export const VENUE_STRIDE = 17;
export interface VenueTable { seed: string; bake: string; keys: string[]; fns: string[]; rows: number[] }

const r2 = (v: number): number => Math.round(v * 100) / 100;
const r4 = (v: number): number => Math.round(v * 10000) / 10000;

/** Bindings → the table the bake writes (positions to the centimetre, yaw to 1e-4 rad; sites ascending, kinds in `VENUE_DEFS` order). */
export function encodeVenues(list: readonly VenueBinding[], seed: string, bake: string): VenueTable {
  const keys: string[] = [], fns: string[] = [];
  const idx = (arr: string[], v: string): number => { let i = arr.indexOf(v); if (i < 0) { i = arr.length; arr.push(v); } return i; };
  const kindIdx = (k: string): number => { for (let i = 0; i < VENUE_DEFS.length; i++) if (VENUE_DEFS[i].kind === k) return i; return 0; };
  const sorted = [...list].sort((a, b) => a.site - b.site || kindIdx(a.kind) - kindIdx(b.kind));
  const rows: number[] = [];
  for (const b of sorted) {
    rows.push(
      b.site, kindIdx(b.kind), b.index, idx(keys, b.key), r2(b.x), r2(b.z), r4(b.yaw), r2(b.w), r2(b.d), r2(b.h), r2(b.gy),
      b.cls, idx(fns, b.fn), r2(b.slotX), r2(b.slotZ), r2(b.streetX), r2(b.streetZ),
    );
  }
  return { seed, bake, keys, fns, rows };
}
export function decodeVenues(t: VenueTable): VenueBinding[] {
  const out: VenueBinding[] = [];
  const r = t.rows;
  for (let i = 0; i + VENUE_STRIDE <= r.length; i += VENUE_STRIDE) {
    const def = VENUE_DEFS[r[i + 1]];
    if (!def) continue;
    out.push({
      site: r[i], kind: def.kind, index: r[i + 2], key: t.keys[r[i + 3]] ?? '', x: r[i + 4], z: r[i + 5], yaw: r[i + 6],
      w: r[i + 7], d: r[i + 8], h: r[i + 9], gy: r[i + 10], cls: r[i + 11], fn: t.fns[r[i + 12]] ?? '',
      slotX: r[i + 13], slotZ: r[i + 14], streetX: r[i + 15], streetZ: r[i + 16],
    });
  }
  return out;
}

// ─── Lookups (a table is a couple of hundred rows: a walk is cheaper than an index is to keep) ────────────────────────

/** The binding of `kind` whose SLOT is (x, z) — what a post row (`venueX/venueZ`) names. */
export function venueAtSlot(list: readonly VenueBinding[], kind: string, x: number, z: number, tol: number = VENUE_SLOT_TOL_M): VenueBinding | null {
  let best: VenueBinding | null = null, bd = tol;
  for (const b of list) {
    if (b.kind !== kind) continue;
    const d = Math.hypot(b.slotX - x, b.slotZ - z);
    if (d <= bd) { bd = d; best = b; }
  }
  return best;
}
/** The bound building of `kind` a point is AT (inside the box + pad); the nearest centre when two pads overlap. */
export function venueAround(list: readonly VenueBinding[], kind: string, x: number, z: number, pad: number = VENUE_PAD_M): VenueBinding | null {
  let best: VenueBinding | null = null, bd = Infinity;
  for (const b of list) {
    if (b.kind !== kind || Math.abs(b.x - x) > 80 || Math.abs(b.z - z) > 80 || !insideVenue(b, x, z, pad)) continue;
    const d = Math.hypot(b.x - x, b.z - z);
    if (d < bd) { bd = d; best = b; }
  }
  return best;
}
/** The binding a registered building IS: the same site, the same seat (and the same model when the caller knows it). */
export function venueOfBuilding(list: readonly VenueBinding[], site: number, x: number, z: number, key?: string, tol: number = VENUE_MATCH_TOL_M): VenueBinding | null {
  for (const b of list) {
    if (b.site !== site || (key !== undefined && b.key !== key)) continue;
    if (Math.hypot(b.x - x, b.z - z) <= tol) return b;
  }
  return null;
}
/** `settle:<site>:settle:<site>:<n>` → the site id; null for every other namespace (a deed never binds). */
export function venueSiteOfId(id: string): number | null {
  if (id.slice(0, 7) !== 'settle:') return null;
  const end = id.indexOf(':', 7);
  const n = Number(end < 0 ? id.slice(7) : id.slice(7, end));
  return Number.isInteger(n) && n >= 0 ? n : null;
}
/** The pack a settlement building was built from names its builders: `civa_*` → 'a', `civb_*` → 'b', else ''. */
export function venuePackCiv(key: string): '' | 'a' | 'b' {
  const k = key.toLowerCase();
  if (k.indexOf('civa_') >= 0) return 'a';
  if (k.indexOf('civb_') >= 0) return 'b';
  return '';
}

/**
 * THE PRESENCE RULE of a post (`takeServicePost`, `postDutyPing`, `servePostRequest`): with a binding the holder must
 * be AT the building; without one, within `slotRadius` of the slot — today's rule, unchanged.
 */
export function atPostVenue(b: VenueBinding | null, slotX: number, slotZ: number, slotRadius: number, x: number, z: number): boolean {
  if (b) return insideVenue(b, x, z);
  return Math.hypot(slotX - x, slotZ - z) <= slotRadius;
}
/** May the holder at (hx, hz) serve the customer at (cx, cz)? Within `reach` of them (today's rule, bound or not), or —
 *  bound — the customer ARRIVED at the door cell while the holder stands at the venue. */
export function mayServeAt(b: VenueBinding | null, reach: number, hx: number, hz: number, cx: number, cz: number): boolean {
  if (Math.hypot(cx - hx, cz - hz) <= reach) return true;
  if (!b) return false;
  return insideVenue(b, hx, hz) && Math.hypot(cx - b.streetX, cz - b.streetZ) <= VENUE_ARRIVE_M;
}
/** Where a customer is sent: the door cell (a hashed step along the street side, `u` in 0..1) or, unbound, 1.6 m off the slot. */
export function customerSpot(b: VenueBinding | null, slotX: number, slotZ: number, u: number): { x: number; z: number } {
  const ang = u * Math.PI * 2;
  if (!b) return { x: slotX + Math.cos(ang) * 1.6, z: slotZ + Math.sin(ang) * 1.6 };
  return { x: b.streetX + Math.cos(ang) * 0.6, z: b.streetZ + Math.sin(ang) * 0.6 };
}
